import { Component, ViewChild, ViewContainerRef, ViewEncapsulation, OnDestroy, ElementRef } from "@angular/core";
import { IDoesFilterPassParams, IFilterParams, RowNode } from "ag-grid-community";
import { IFilterAngularComp } from "ag-grid-angular";
import { CustomColDef, DateRangeFilterOptions, NumberRangeFilterOptions, FilterOption } from "../custom-colDef";
import { DropdownComponent } from "@bssh/comp-lib";
import { BsshDataTableMessageService } from "../services/bssh-data-table-message.service";
import { FILTER_DROPDOWN_POSITIONS } from "./constants";
import { Subject, BehaviorSubject } from "rxjs";
import get from "lodash/get";
import { SubSink } from 'subsink';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { debounceTime, switchMap } from 'rxjs/operators';
import { isNullOrUndefined } from 'util';
import { BsDatepickerDirective } from "ngx-bootstrap/datepicker";
import { DateRangeComponent } from "@bssh/comp-lib";

// https://www.ag-grid.com/javascript-grid-filter-component/#filter-component-interface-ifiltercomp
@Component({
  selector: "lib-custom-filter",
  templateUrl: "./custom-filter.component.html",
  styleUrls: ["./custom-filter.component.scss"],
  encapsulation: ViewEncapsulation.None
})
export class CustomFilterComponent implements IFilterAngularComp, OnDestroy {
  @ViewChild(DropdownComponent, { static: true })
  public dropdown: DropdownComponent;
  public dropdownPositions: ConnectedPosition[] = FILTER_DROPDOWN_POSITIONS;

  // Need to set a custom class for popups
  // https://www.ag-grid.com/angular-data-grid/component-filter/#custom-filters-containing-a-popup-element
  public customClass = "ag-custom-component-popup";

  @ViewChild("filterOptionList", { static: false })
  public filterOptionList: ElementRef;

  @ViewChild("dateRange", { static: false })
  public dateRange: DateRangeComponent;

  searchBoxInput: string = '';
  updatedUserInput: Subject<string> = new Subject<string>();
  updatedNumberRange: Subject<{from: number, to: number}> = new Subject<{from: number, to: number}>();

  private valueGetter: (rowNode: RowNode) => any;
  public params: IFilterParams;
  public selectedFilterOption: FilterOption;
  public text = "";
  public selectedDateRange: DateRangeFilterOptions = null;
  public selectedNumberRange: NumberRangeFilterOptions = null;
  public numberRangeFrom: string = null;
  public numberRangeTo: string = null;

  @ViewChild("input", { read: ViewContainerRef, static: true })
  public input: ViewContainerRef;

  public activeSort: string;
  private filterAllOptions: FilterOption[] = []; // all options available
  public filterDisplayOptions$ = new BehaviorSubject<FilterOption[]>([]); // options that are actually visible to user
  public headerName: string;
  public colDef: CustomColDef;
  public panelClass = ["drop-down__filter"];
  
  // Default filter configuration for all columns, unless overwritten by the config defined in column definition
  public filterConfig: any = {
      minHeight: '93px',
      maxHeight: '340px',
      minWidth: '170px',
      maxWidth: '170px',
      height: 'auto',
      panelClass: this.panelClass
    };

  public dateRangeInputConfig: any = {};
  public dateRangeConfigs: any = {
    showWeekNumbers: false,
    rangeInputFormat: "YYYY-MM-DD"
  };

  private _subs: SubSink = new SubSink();

  @ViewChild("filterTextField", { static: false })
  public filterTextField: ElementRef;

  constructor(
    private messageService: BsshDataTableMessageService,
  ) { }

  get dateRangePlacement(): BsDatepickerDirective["placement"] {
    // Default placement to bottom
    if (!this.colDef.dateRangeFilterOptions || !this.colDef.dateRangeFilterOptions.placement) {
      return "bottom";
    }

    return this.colDef.dateRangeFilterOptions.placement;
  }

  agInit(params: IFilterParams): void {
    this.params = params;
    this.colDef = this.params.colDef;
    this.valueGetter = params.valueGetter;

    if (this.params.rowModel.constructor.name === "ServerSideRowModel") {
      this.panelClass.push("server-side");
    }

    if (this.colDef.filterWithSearchBox) {
      // if callback provided then use it, other wise filter locally
      if (typeof this.colDef.updateFilterOptionsCallback === 'function') {
        this.debounceUserInputAndUpdateFilterOptionsAsync();
      } else {
        this.debounceUserInputAndUpdateFilterOptionsLocally();
      }
    }

    // Use provided filterOptions if available, otherwise collect filter options from data
    if (this.colDef.filterOptions) {

      this._subs.sink = this.colDef.filterOptions.subscribe((list) => {
        this.setFilterOptions(list);
      });

    } else {
      this.collectFilterOptionsFromData();
    }
    this.headerName = this.colDef.headerName;
    this.filterConfig.height = this.colDef.filterActive ? 'auto' :
              this.colDef.dateRangeFilterActive ? '148px' :
              this.colDef.numberRangeFilterActive ? '148px' :
              '93px'

    // Set up number range filter debouncing
    if (this.colDef.numberRangeFilterActive) {
      this._subs.sink = this.updatedNumberRange
        .pipe(debounceTime(500))
        .subscribe((range: {from: number, to: number}) => {
          this.selectedNumberRange = {
            filterFromValue: range.from,
            filterToValue: range.to
          };
          this.params.filterChangedCallback({ sorting: false });
          this.messageService.sendMessage("headerNameUpdated");
        });
    }

    // Build the effective filterConfig for this column:
    // 1. Start from the default filterConfig.
    // 2. Apply any user-specified overrides from colDef.filterConfig.
    //    - This lets developers change only the properties they care about
    //      for a given column, without redefining the whole config.
    Object.assign(this.filterConfig, this.colDef.filterConfig);
  }

  private collectFilterOptionsFromData() {
    // Note: this method only collects options available in the page when this method is called!
    const filterListItems: Set<string> = new Set();
    this.params.rowModel.forEachNode((rowData) => {
      const filterItem = get(rowData, `data.${this.colDef.field}`);
      if (filterItem) {
        filterListItems.add(filterItem);
      }
    });
    // Transform items from rows into filterOption objects
    const filterOptions: FilterOption[] = [...filterListItems].map(option => {
      return { displayName: option, filterValue: option };
    });
    this.setFilterOptions(filterOptions);
  }

  isFilterActive(): boolean {
    return this.text !== null && this.text !== undefined && this.text !== "";
  }

  doesFilterPass(params: IDoesFilterPassParams): boolean {
    return this.text.toLowerCase()
      .split(" ")
      .every((filterWord) => {
        return this.valueGetter(params.node).toString().toLowerCase().indexOf(filterWord) >= 0;
      });
  }

  // Gets the filter state. If filter is not active, then should return null/undefined.
  // The grid calls getModel() on all active filters when gridApi.getFilterModel() is called.
  getModel(): any {
    if (this.selectedFilterOption) {
      return { value: this.selectedFilterOption.filterValue, displayName: this.selectedFilterOption.displayName };
    }

    if (this.selectedDateRange) {
      return { fromValue: this.selectedDateRange.filterFromValue, toValue: this.selectedDateRange.filterToValue };
    }

    if (this.selectedNumberRange) {
      return { fromValue: this.selectedNumberRange.filterFromValue, toValue: this.selectedNumberRange.filterToValue };
    }

    return null;
  }

  setModel(model: any): void {
    // model is undefined when filter is cleared or not active
    // When filter is cleared via Grid's filter api, the model is null.
    if (model == null) {
      this.text = "";
      this.selectedFilterOption = null;
      this.selectedDateRange = null;
      this.selectedNumberRange = null;
      this.numberRangeFrom = null;
      this.numberRangeTo = null;
      if (this.dateRange) {
        this.dateRange.clear();
      }
    } else if (model.filterValue != null) {
      this.selectedFilterOption = model;
    } else if (model.fromValue != null || model.toValue != null) {
      // Handle both date range and number range models
      if (this.colDef.numberRangeFilterActive) {
        this.selectedNumberRange = {
          filterFromValue: model.fromValue,
          filterToValue: model.toValue
        };
        this.numberRangeFrom = model.fromValue;
        this.numberRangeTo = model.toValue;
      }
    }
  }

  onChange(selectedFilterOption: FilterOption): void {
    if (this.text !== selectedFilterOption.displayName) {
      this.text = selectedFilterOption.displayName;
      this.selectedFilterOption = selectedFilterOption;
      this.messageService.sendMessage("headerNameUpdated");
      this.params.filterChangedCallback({ sorting: false });
    }
    this.dropdown.hide();
  }

  onDateRangeChange($event): void {
    if ($event) {
      this.selectedDateRange = {
        filterFromValue: $event[0],
        filterToValue: $event[1],
      };
      this.params.filterChangedCallback({ sorting: false });
      this.dropdown.hide();
    } else {
      this.selectedDateRange = null;
    }

    this.messageService.sendMessage("headerNameUpdated");
  }

  onNumberRangeChange(type: 'from' | 'to', value: string): void {
    const numValue = value === '' || value == null ? null : parseFloat(value);
    
    if (type === 'from') {
      this.numberRangeFrom = String(numValue);
    } else {
      this.numberRangeTo = String(numValue);
    }

    this.updatedNumberRange.next({
      from: this.numberRangeFrom ? Number(this.numberRangeFrom) : null,
      to: this.numberRangeTo ? Number(this.numberRangeTo) : null
    });
  }

  afterGuiAttached(params): void {
    this.activeSort = this.params.column.getSort();
    
    // Restore number range values if they exist BEFORE showing dropdown
    if (this.colDef.numberRangeFilterActive && this.selectedNumberRange) {
      this.numberRangeFrom = String(this.selectedNumberRange.filterFromValue);
      this.numberRangeTo = String(this.selectedNumberRange.filterToValue);
    }
    
    setTimeout(() => this.dropdown.show(), 0);

    // put focus to search box
    if (this.colDef.filterWithSearchBox) {
      setTimeout(() => this.filterTextField.nativeElement.focus());
    }
  }

  onSortRequest(order: string, event): void {
    this.activeSort = order;

    // enforce single column sorting only
    const newSortModel = [
      {
        colId: this.params.column.getColId(),
        sort: order
      }
    ];
    this.params.api.setSortModel(newSortModel);
    this.messageService.sendMessage("orderingUpdated");
    this.dropdown.hide();
  }

  public ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  private debounceUserInputAndUpdateFilterOptionsAsync() {
    this._subs.sink = this.updatedUserInput
      .pipe(debounceTime(500))
      .pipe(switchMap((updatedData: string) => {
        return this.colDef.updateFilterOptionsCallback(updatedData);
      }))
      .subscribe((updatedFilterOptions: FilterOption[]) => {
        this.setFilterOptions(updatedFilterOptions);
      });
  }

  private debounceUserInputAndUpdateFilterOptionsLocally() {
    this._subs.sink = this.updatedUserInput
      .pipe(debounceTime(500))
      .subscribe(updatedData => {
        this.updateFilterOptions(updatedData);
      });
  }

  /* Method to update filter options locally based on user input and adjust height
   * TODO: improve logic.
   */
  private updateFilterOptions(arg: string) {
    if (arg === '') {
      this.updateFilterDisplayOptions(this.filterAllOptions);
      return;
    }
    const filteredArray = this.filterAllOptions ?
      this.filterAllOptions.filter((item: FilterOption) =>
        item.displayName.toLowerCase().includes(arg.toLowerCase()))
      : [];
    this.updateFilterDisplayOptions(filteredArray);
  }

  private setFilterOptions(optionList: FilterOption[]) {
    if (isNullOrUndefined(optionList)) {
      return;
    }
    this.filterAllOptions = optionList;
    this.updateFilterDisplayOptions(optionList);
  }

  private updateFilterDisplayOptions(filteredOptionList: FilterOption[]) {
    if (isNullOrUndefined(filteredOptionList)) {
      return;
    }
    this.filterDisplayOptions$.next(filteredOptionList);
  }

  onChangeInputValue(changedData) {
    this.updatedUserInput.next(changedData);
  }

  getFilterListScrollClass() {
    if (this.colDef.sortable) {
      return this.colDef.filterWithSearchBox ? 'sorting-search-filter-scroll' : 'sorting-simple-filter-scroll';
    } else {
      return this.colDef.filterWithSearchBox ? 'search-filter-scroll' : 'simple-filter-scroll';
    }
  }
}